import React from 'react';
import * as styles from './Button.css.ts';

/**
 * Buttonコンポーネントのプロパティ
 */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** ボタンのバリアント。デフォルトは'secondary' */
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'success' | 'warning';
  /** ボタンのサイズ。デフォルトは'md' */
  size?: 'sm' | 'md' | 'lg';
  /** trueの場合、コンテナの幅いっぱいに表示 */
  fullWidth?: boolean;
  /** trueの場合、ローディングスピナーを表示してボタンを無効化 */
  loading?: boolean;
  /** 表示するアイコン（オプション） */
  icon?: React.ReactNode;
  /** テキストに対するアイコンの位置。デフォルトは'left' */
  iconPosition?: 'left' | 'right';
}

/**
 * 複数のバリアントとサイズを持つ再利用可能なボタンコンポーネント
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * <Button variant="danger" loading={isLoading}>
 *   Delete
 * </Button>
 *
 * <Button variant="ghost" icon={<Icon />} iconPosition="left">
 *   With Icon
 * </Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  className = '',
  children,
  disabled,
  ...rest
}) => {
  const buttonClass = styles.button({
    variant,
    size,
    fullWidth: fullWidth || undefined,
    loading: loading || undefined,
  });

  return (
    <button
      className={`${buttonClass} ${className}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <span className={styles.spinner} />}
      {!loading && icon && iconPosition === 'left' && (
        <span className={styles.icon}>{icon}</span>
      )}
      {children && <span className={styles.content}>{children}</span>}
      {!loading && icon && iconPosition === 'right' && (
        <span className={styles.icon}>{icon}</span>
      )}
    </button>
  );
};